import { useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'

interface Props {
  email: string
}

export default function ForcePasswordChange() {
  const { email } = usePage().props as unknown as Props
  const [form, setForm] = useState({ password: '', password_confirmation: '' })
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (form.password.length < 6) {
      setErrors({ password: 'Password must be at least 6 characters' })
      return
    }
    if (form.password !== form.password_confirmation) {
      setErrors({ password_confirmation: 'Passwords do not match' })
      return
    }
    setSubmitting(true)
    setErrors({})
    router.patch('/password_change', form, {
      onFinish: () => setSubmitting(false),
      onError: (errs) => setErrors(errs as Record<string, string>),
    })
  }

  return (
    <>
      <Head title="Choose a New Password" />
      <div className="min-h-screen bg-stone/10 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img src="/pinnacle-logo.png" alt="Pinnacle" className="w-14 h-14 rounded-full mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-text-primary font-heading">Choose a new password</h1>
            <p className="text-base text-text-secondary mt-1">
              You signed in with a temporary password. Set your own password to continue.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-secondary bg-stone/10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">New password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal ${
                    errors.password ? 'border-pinnacle-red' : 'border-stone/30'
                  }`}
                  required
                  minLength={6}
                />
                {errors.password && <p className="text-sm text-pinnacle-red mt-1">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Confirm new password</label>
                <input
                  type="password"
                  value={form.password_confirmation}
                  onChange={e => setForm(prev => ({ ...prev, password_confirmation: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal ${
                    errors.password_confirmation ? 'border-pinnacle-red' : 'border-stone/30'
                  }`}
                  required
                />
                {errors.password_confirmation && <p className="text-sm text-pinnacle-red mt-1">{errors.password_confirmation}</p>}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-pinnacle-red text-white text-base font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Set new password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
